/**
 * Create / edit page for a custom origin rule, modelled on the firewall rule
 * builder. Reached from the list via `origin/new?domain=` and
 * `origin/edit?domain=&id=`.
 *
 * The condition builder is driven by the rule-builder schema
 * (`GET /waf/origin/schema`) — identical to firewall. The action is a single
 * proxy-pass origin config (origin type + sources + ports + SSL/SNI), following
 * the canonical contract enforced by the backend.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Server, ArrowLeft, Trash2, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafOriginRuleSchema,
  useWafOriginRules,
  useCreateOriginRule,
  useUpdateOriginRule,
  wafExtractError,
  type OriginRule,
  type OriginRuleAction,
  type OriginRuleCondition,
  type OriginRuleInput,
  type OriginField,
  type OriginOperator,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { MultiSelect, type MultiSelectOption } from '../_shared/MultiSelect';
import { geoCountries, humanizeGeoName, acGeoCode } from '../_shared/geo';
import styles from './WafOriginRules.module.css';

/** Country options for geo operators (value = "{continent}-{country}-00-00-00"). */
const GEO_OPTIONS: MultiSelectOption[] = geoCountries
  .map((c) => ({ value: acGeoCode(c), label: humanizeGeoName(c.name) }))
  .sort((a, b) => a.label.localeCompare(b.label));

/* ---- Condition value (IP) validation, shared with the firewall builder ---- */

/** The "Remote address" / "Real client IP" fields expect an IP value. */
const fieldIsIp = (f?: OriginField) => !!f && /remote|client|ip[\s_-]?addr/i.test(`${f.value} ${f.label}`);

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/** A plain IPv4 address, optionally with a CIDR prefix (/0–32). */
function isIpAddress(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  const slash = s.indexOf('/');
  if (slash === -1) return IPV4.test(s);
  const addr = s.slice(0, slash);
  const prefix = s.slice(slash + 1);
  return IPV4.test(addr) && /^\d{1,2}$/.test(prefix) && Number(prefix) <= 32;
}

/**
 * Free-form list operators (multi-value, no fixed options) — mirror the SDK's
 * `fwListOps`. Their value is a typed list of strings stored under `values`.
 */
const LIST_OPERATORS = new Set(['pm', '!pm', 'isIn', '!isIn']);
const opIsList = (op?: OriginOperator) => !!op && LIST_OPERATORS.has(op.value);

/* ----------------------------- Action constants ----------------------------- */

const ORIGIN_TYPES = [
  { value: 'FQDN', label: 'FQDN (hostname)' },
  { value: 'IPv4', label: 'IPv4 address' },
];
const SSL_POLICY_OPTIONS = [
  { value: 'Match View', label: 'Match View' },
  { value: 'HTTP', label: 'HTTP' },
  { value: 'HTTPS', label: 'HTTPS' },
];
const BALANCE_MODES = [
  { value: 'consistent', label: 'Consistent hash' },
  { value: 'roundrobin', label: 'Round robin' },
];

/** A basic FQDN (one or more labels + a TLD). */
const FQDN = /^(?=.{1,253}$)([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;

const portInRange = (raw: string) => {
  if (raw.trim() === '') return true; // optional; emptiness handled separately
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 65535;
};

/** One condition row in the builder. `logic` connects to the NEXT row. */
interface Row {
  field: string;
  key: string;
  operator: string;
  value: string;
  values: string[];
  logic: string;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint ? <span className={styles.subHint}>{hint}</span> : null}
    </div>
  );
}

export function WafOriginRuleForm({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const id = params.get('id');
  const isEdit = !!id;
  const back = () => navigate(`/services/waf/${serviceId}/origin`);

  const schemaQ = useWafOriginRuleSchema(serviceId);
  const fields = schemaQ.data?.fields ?? [];

  const rulesQ = useWafOriginRules(serviceId, isEdit ? domain : undefined);
  const rule = useMemo<OriginRule | undefined>(
    () => (isEdit ? (rulesQ.data ?? []).find((r) => r.id === id) : undefined),
    [isEdit, rulesQ.data, id],
  );

  const fieldFor = (f: string): OriginField | undefined => fields.find((x) => x.value === f);
  const opFor = (r: Row): OriginOperator | undefined => fieldFor(r.field)?.operators.find((o) => o.value === r.operator);

  const blankRow = (): Row => {
    const f0 = fields[0];
    const o0 = f0?.operators[0];
    return { field: f0?.value ?? '', key: '', operator: o0?.value ?? '', value: '', values: [], logic: 'and' };
  };

  // ---- form state ----
  const [seeded, setSeeded] = useState(false);
  const [name, setName] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  // proxy-pass action
  const [originType, setOriginType] = useState('FQDN');
  const [sources, setSources] = useState<string[]>(['']);
  const [sslPolicy, setSslPolicy] = useState('Match View');
  const [portHttp, setPortHttp] = useState('80');
  const [portHttps, setPortHttps] = useState('443');
  const [balanceMode, setBalanceMode] = useState('consistent');
  const [sni, setSni] = useState(false);
  const [upsHost, setUpsHost] = useState('');
  // Validation hints stay hidden until the first submit attempt.
  const [submitted, setSubmitted] = useState(false);

  // Seed once the schema (and, on edit, the rule) is ready.
  useEffect(() => {
    if (seeded) return;
    if (schemaQ.isLoading || fields.length === 0) return;
    if (isEdit) {
      if (rulesQ.isLoading) return;
      if (rule) seedFrom(rule);
      setSeeded(true);
    } else {
      setRows([blankRow()]);
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded, schemaQ.isLoading, fields.length, isEdit, rule, rulesQ.isLoading]);

  function seedFrom(r: OriginRule) {
    setName(r.name);
    const seededRows: Row[] = (r.conditions ?? []).map((c) => ({
      field: c.field,
      key: c.key ?? '',
      operator: c.operator,
      value: c.value ?? '',
      values: c.values ?? [],
      logic: c.logic || 'and',
    }));
    setRows(seededRows.length ? seededRows : [blankRow()]);
    const a = r.action;
    if (a.origin_type) setOriginType(a.origin_type);
    setSources(a.sources?.length ? a.sources : ['']);
    if (a.ssl_policies?.length) {
      const set = a.ssl_policies;
      // "Match View" is the meta-option that maps to both HTTP + HTTPS.
      setSslPolicy(set.includes('HTTP') && set.includes('HTTPS') ? 'Match View' : set[0]);
    }
    if (a.port_http != null) setPortHttp(String(a.port_http));
    if (a.port_https != null) setPortHttps(String(a.port_https));
    if (a.balance_mode) setBalanceMode(a.balance_mode);
    if (a.sni != null) setSni(a.sni);
    if (a.ups_host) setUpsHost(a.ups_host);
  }

  // ---- row mutations ----
  const patchRow = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const changeField = (i: number, field: string) => {
    const op0 = fieldFor(field)?.operators[0]?.value ?? '';
    patchRow(i, { field, operator: op0, key: '', value: '', values: [] });
  };
  const changeOperator = (i: number, operator: string) => patchRow(i, { operator, value: '', values: [] });

  const addRow = (logic: string) => {
    if (rows.length >= 5) return;
    setRows((rs) => {
      const next = rs.map((r, idx) => (idx === rs.length - 1 ? { ...r, logic } : r));
      return [...next, blankRow()];
    });
  };
  const removeRow = (i: number) => setRows((rs) => (rs.length <= 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const setLogic = (rowIdx: number, logic: string) => patchRow(rowIdx - 1, { logic });

  // ---- source mutations ----
  // FQDN proxies to a single hostname; IPv4 allows a pool of addresses.
  const singleSource = originType === 'FQDN';
  const changeOriginType = (v: string) => {
    setOriginType(v);
    if (v === 'FQDN') setSources((s) => [s[0] ?? '']); // collapse to one
  };
  const setSource = (i: number, v: string) => setSources((s) => s.map((x, idx) => (idx === i ? v : x)));
  const addSource = () => setSources((s) => [...s, '']);
  const removeSource = (i: number) => setSources((s) => (s.length <= 1 ? s : s.filter((_, idx) => idx !== i)));

  // ---- build + validate (conditions) ----
  const opHasOptions = (op?: OriginOperator) => op?.type === 'geo' || !!op?.options?.length || opIsList(op);
  const keyMissing = (r: Row) => !!fieldFor(r.field)?.input_value && !r.key.trim();
  const valueMissing = (r: Row) => {
    const op = opFor(r);
    if (!op || op.input_value === false) return false; // operator takes no value
    return opHasOptions(op) ? r.values.length === 0 : !r.value.trim();
  };
  const valueBadFormat = (r: Row) => {
    const op = opFor(r);
    if (!op || op.input_value === false || opHasOptions(op)) return false;
    if (!fieldIsIp(fieldFor(r.field)) || !r.value.trim()) return false;
    return !isIpAddress(r.value);
  };
  const rowComplete = (r: Row) =>
    !!r.field && !!r.operator && !keyMissing(r) && !valueMissing(r) && !valueBadFormat(r);

  const validRows = rows.filter((r) => r.field && r.operator);

  function buildConditions(): OriginRuleCondition[] {
    return validRows.map((r, idx) => {
      const op = opFor(r);
      const hasOptions = opHasOptions(op);
      const cond: OriginRuleCondition = { field: r.field, operator: r.operator };
      if (fieldFor(r.field)?.input_value && r.key.trim()) cond.key = r.key.trim();
      if (op?.input_value !== false) {
        if (hasOptions) cond.values = r.values;
        else cond.value = r.value.trim();
      }
      if (idx < validRows.length - 1) cond.logic = r.logic || 'and';
      return cond;
    });
  }

  // ---- build + validate (action) ----
  const sourceBad = (s: string) => {
    const v = s.trim();
    if (!v) return false;
    return originType === 'IPv4' ? !IPV4.test(v) : !FQDN.test(v);
  };
  const sourcesProvided = sources.map((s) => s.trim()).filter(Boolean);
  const anyPort = Number(portHttp) > 0 || Number(portHttps) > 0;

  const actionValid =
    sourcesProvided.length >= 1 &&
    !sources.some(sourceBad) &&
    anyPort &&
    portInRange(portHttp) &&
    portInRange(portHttps);

  function buildAction(): OriginRuleAction {
    const a: OriginRuleAction = { type: 'proxyPass', origin_type: originType, sources: sourcesProvided };
    // "Match View" means: connect with whichever scheme the viewer used → send both.
    if (sslPolicy) a.ssl_policies = sslPolicy === 'Match View' ? ['HTTP', 'HTTPS'] : [sslPolicy];
    if (Number(portHttp) > 0) a.port_http = Number(portHttp);
    if (Number(portHttps) > 0) a.port_https = Number(portHttps);
    if (balanceMode) a.balance_mode = balanceMode;
    a.sni = sni;
    if (upsHost.trim()) a.ups_host = upsHost.trim();
    return a;
  }

  const valid =
    !!name.trim() && rows.length >= 1 && rows.length <= 5 && rows.every(rowComplete) && actionValid;

  const create = useCreateOriginRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule created' }); trackFeatureAction('waf', 'origin_rule', 'create'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateOriginRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule updated' }); trackFeatureAction('waf', 'origin_rule', 'edit'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const pending = create.isPending || update.isPending;

  const submit = () => {
    setSubmitted(true);
    if (!valid) return;
    const input: OriginRuleInput = {
      name: name.trim(),
      enable: rule ? rule.enable : true,
      action: buildAction(),
      conditions: buildConditions(),
    };
    if (isEdit && rule) update.mutate({ domain, id: rule.id, input });
    else create.mutate({ domain, input });
  };

  // Human-readable summary of the matching conditions.
  const summary = useMemo(() => {
    const parts = validRows.map((r) => {
      const f = fieldFor(r.field);
      const o = opFor(r);
      const isGeo = o?.type === 'geo';
      const v = isGeo
        ? r.values.map((x) => GEO_OPTIONS.find((g) => g.value === x)?.label ?? x).join(', ')
        : opIsList(o) || o?.options?.length ? r.values.join(', ') : r.value;
      return `${f?.label ?? r.field} ${o?.label ?? r.operator} "${v}"`;
    });
    if (!parts.length) return '';
    return parts.reduce((acc, part, idx) => (idx === 0 ? part : `${acc} ${validRows[idx - 1].logic || 'and'} ${part}`), '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const header = (
    <PageHeader
      icon={<Server size={22} />}
      title={`${isEdit ? 'Edit' : 'Add'} origin rule`}
      description="Select a matching condition and the origin to proxy matched requests to."
      actions={<Button variant="secondary" startIcon={<ArrowLeft size={15} />} onClick={back}>Back</Button>}
    />
  );

  if (!domain) {
    return (
      <Container size="lg">{header}
        <Alert tone="warning" title="No domain selected" actions={<button onClick={back}>Back</button>}>
          Open this form from the Origin Rules list so a domain is selected.
        </Alert>
      </Container>
    );
  }
  if (schemaQ.isError) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Couldn't load the rule builder" actions={<button onClick={() => schemaQ.refetch()}>Retry</button>}>
          The WAF service couldn't return the origin rule schema. Try again.
        </Alert>
      </Container>
    );
  }
  if (!seeded) {
    return <Container size="lg">{header}<Skeleton style={{ height: 460 }} /></Container>;
  }
  if (isEdit && !rule) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Rule not found" actions={<button onClick={back}>Back</button>}>This rule no longer exists.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      {header}
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Edit' : 'New'} origin rule</CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Rule name">
              <Input value={name} placeholder="Rule name" disabled={pending} invalid={submitted && !name.trim()} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 360 }} />
            </Field>

            <div>
              <p className={styles.sectionTitle}>Rule configurations</p>
              <div className={styles.condList}>
                {rows.map((r, i) => {
                  const f = fieldFor(r.field);
                  const op = opFor(r);
                  const ops = f?.operators ?? [];
                  const isGeo = op?.type === 'geo';
                  const isList = opIsList(op);
                  const hasOptions = isGeo || isList || !!op?.options?.length;
                  const selectOptions = isGeo ? GEO_OPTIONS : isList ? [] : (op?.options ?? []).map((o) => ({ value: o, label: o }));
                  return (
                    <div key={i} className={styles.condRow}>
                      <div className={styles.condRail}>
                        {i === 0 ? (
                          <span className={styles.ifChip}>IF</span>
                        ) : (
                          <div className={styles.logicToggle}>
                            <button type="button" className={rows[i - 1].logic !== 'or' ? styles.logicOn : styles.logicOff} disabled={pending} onClick={() => setLogic(i, 'and')}>And</button>
                            <button type="button" className={rows[i - 1].logic === 'or' ? styles.logicOn : styles.logicOff} disabled={pending} onClick={() => setLogic(i, 'or')}>Or</button>
                          </div>
                        )}
                      </div>

                      <div className={styles.condBody}>
                        <div className={styles.condTop}>
                          <Select value={r.field} onValueChange={(v) => changeField(i, v)} disabled={pending}>
                            <SelectTrigger aria-label="Field" className={styles.condField}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {fields.map((fl) => <SelectItem key={fl.value} value={fl.value}>{fl.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {f?.input_value && (
                            <Input value={r.key} placeholder="Filter value" disabled={pending} invalid={submitted && keyMissing(r)} onChange={(e) => patchRow(i, { key: e.target.value })} aria-label="Filter value" className={styles.condFilter} />
                          )}
                          <IconButton variant="ghost" size="sm" aria-label="Remove condition" disabled={pending || rows.length <= 1} onClick={() => removeRow(i)}>
                            <Trash2 size={15} />
                          </IconButton>
                        </div>
                        {f?.description ? <span className={styles.condHint}>{f.description}</span> : null}
                        <div className={styles.condBottom}>
                          <Select value={r.operator} onValueChange={(v) => changeOperator(i, v)} disabled={pending || !ops.length}>
                            <SelectTrigger aria-label="Operator" className={styles.condOp}><SelectValue placeholder="Operator" /></SelectTrigger>
                            <SelectContent>
                              {ops.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {op?.input_value === false ? null : hasOptions ? (
                            <div className={styles.condValue}>
                              <MultiSelect
                                placeholder={isGeo ? 'Select countries…' : isList ? 'Add values…' : 'Value'}
                                options={selectOptions}
                                value={r.values}
                                disabled={pending}
                                invalid={submitted && valueMissing(r)}
                                creatable={isList}
                                onChange={(v) => patchRow(i, { values: v })}
                              />
                            </div>
                          ) : (
                            <Input value={r.value} placeholder={fieldIsIp(f) ? '192.0.2.10' : 'Value'} disabled={pending} invalid={submitted && (valueMissing(r) || valueBadFormat(r))} onChange={(e) => patchRow(i, { value: e.target.value })} aria-label="Value" className={styles.condValue} />
                          )}
                        </div>
                        {submitted && valueBadFormat(r) ? (
                          <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-xs, 12px)' }}>
                            Enter a valid IPv4 address (e.g. 192.0.2.10 or 192.0.2.0/24).
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.addRow}>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending || rows.length >= 5} onClick={() => addRow('and')}>And</Button>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending || rows.length >= 5} onClick={() => addRow('or')}>Or</Button>
                {rows.length >= 5 ? <span className={styles.subHint}>Up to 5 conditions.</span> : null}
              </div>

              {summary ? <div className={styles.summary}>{summary}</div> : null}
            </div>

            <hr className={styles.divider} />
            <p className={styles.sectionTitle}>Then proxy to this origin…</p>

            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Field label="Origin type">
                <div className={styles.segment}>
                  {ORIGIN_TYPES.map((t) => (
                    <Button key={t.value} size="sm" variant={originType === t.value ? undefined : 'secondary'} disabled={pending} onClick={() => changeOriginType(t.value)}>
                      {t.label}
                    </Button>
                  ))}
                </div>
              </Field>

              <Field label={singleSource ? 'Origin source' : 'Origin sources'} hint={singleSource ? 'A single hostname (FQDN).' : 'One IPv4 address per row.'}>
                <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  {sources.map((s, i) => (
                    <div key={i} className={styles.sourceRow}>
                      <Input
                        className={styles.grow}
                        value={s}
                        placeholder={singleSource ? 'origin.example.com' : '203.0.113.10'}
                        disabled={pending}
                        invalid={submitted && (sourceBad(s) || (sourcesProvided.length === 0))}
                        aria-label={singleSource ? 'Origin source' : `Origin source ${i + 1}`}
                        onChange={(e) => setSource(i, e.target.value)}
                      />
                      {!singleSource && (
                        <IconButton variant="ghost" size="sm" aria-label="Remove source" disabled={pending || sources.length <= 1} onClick={() => removeSource(i)}>
                          <Trash2 size={15} />
                        </IconButton>
                      )}
                    </div>
                  ))}
                  {!singleSource && (
                    <div>
                      <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending} onClick={addSource}>Add source</Button>
                    </div>
                  )}
                  {submitted && sourcesProvided.length === 0 ? (
                    <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-xs, 12px)' }}>
                      Add at least one origin source.
                    </span>
                  ) : null}
                </div>
              </Field>

              <div className={styles.grid2}>
                <Field label="HTTP port">
                  <Input type="number" min={1} max={65535} className={styles.noSpin} value={portHttp} placeholder="80" disabled={pending} invalid={submitted && (!portInRange(portHttp) || !anyPort)} onChange={(e) => setPortHttp(e.target.value)} />
                </Field>
                <Field label="HTTPS port">
                  <Input type="number" min={1} max={65535} className={styles.noSpin} value={portHttps} placeholder="443" disabled={pending} invalid={submitted && (!portInRange(portHttps) || !anyPort)} onChange={(e) => setPortHttps(e.target.value)} />
                </Field>
              </div>
              {submitted && !anyPort ? (
                <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-xs, 12px)' }}>
                  Set at least one of HTTP / HTTPS port.
                </span>
              ) : null}

              <div className={styles.grid2}>
                <Field label="SSL policy" hint="Which scheme to use when connecting to the origin.">
                  <Select value={sslPolicy} onValueChange={setSslPolicy} disabled={pending}>
                    <SelectTrigger aria-label="SSL policy"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SSL_POLICY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Balance mode" hint="How requests are spread across multiple sources.">
                  <Select value={balanceMode} onValueChange={setBalanceMode} disabled={pending}>
                    <SelectTrigger aria-label="Balance mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BALANCE_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className={styles.grid2}>
                <Field label="Custom host header" hint="Optional. Overrides the Host header sent to the origin.">
                  <Input value={upsHost} placeholder="origin.example.com" disabled={pending} onChange={(e) => setUpsHost(e.target.value)} />
                </Field>
                <Field label="SNI" hint="Send Server Name Indication on the TLS handshake to the origin.">
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', paddingTop: 6 }}>
                    <Switch checked={sni} disabled={pending} aria-label="Enable SNI" onCheckedChange={setSni} />
                    <span className={styles.subHint}>{sni ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </Field>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', justifyContent: 'flex-end', marginTop: 'var(--vn-space-2)' }}>
              {submitted && !valid ? (
                <span style={{ marginRight: 'auto', color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-sm)' }}>
                  Please complete the highlighted fields.
                </span>
              ) : null}
              <Button variant="secondary" onClick={back}>Cancel</Button>
              <Button onClick={submit} disabled={pending}>{isEdit ? 'Save changes' : 'Create'}</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}

export default WafOriginRuleForm;
